import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Chip,
  Stack
} from '@mui/material';

const SettingsActionCard = React.memo(({
  icon,
  title,
  description,
  buttonText,
  buttonVariant = 'contained',
  buttonColor = 'primary',
  onClick,
  disabled = false,
  statusChip,
  destructive = false
}) => {
  const cardStyles = useMemo(() => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: destructive ? '1px solid #d32f2f' : '1px solid #e0e0e0',
    borderRadius: 2,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: destructive ? '0 4px 12px rgba(211, 47, 47, 0.15)' : '0 4px 12px rgba(113, 45, 148, 0.1)',
      transform: 'translateY(-2px)'
    }
  }), [destructive]);

  const buttonStyles = useMemo(() => ({
    backgroundColor: destructive ? '#d32f2f' : buttonColor === 'primary' ? '#712d94' : undefined,
    '&:hover': {
      backgroundColor: destructive ? '#b71c1c' : buttonColor === 'primary' ? '#5a2470' : undefined,
    },
    '&:disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(0, 0, 0, 0.26)',
    }
  }), [destructive, buttonColor]);

  return (
    <Card sx={cardStyles}>
      <CardContent sx={{ flexGrow: 1, pb: 1, px: 2, pt: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          {icon}
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {title}
          </Typography>
          {statusChip && (
            <Chip 
              label={statusChip.label} 
              color={statusChip.color} 
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {description}
        </Typography>
      </CardContent>
      <CardActions sx={{ pt: 0, px: 2, pb: 2, gap: 1 }}>
        <Button
          variant={buttonVariant}
          color={buttonColor}
          onClick={onClick}
          disabled={disabled}
          fullWidth
          sx={buttonStyles}
        >
          {buttonText}
        </Button>
      </CardActions>
    </Card>
  );
});

SettingsActionCard.displayName = 'SettingsActionCard';

export default SettingsActionCard;
